import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, BookOpen, Lightbulb, Compass, Pencil, MessageSquare, GraduationCap, Target } from "lucide-react";
import { BABASAHEB_IMG } from "../components/BabasahebPortrait";

const META = {
  series: "Ambedkarite Buddhist Educational Series, UK Edition",
  level: "Junior Programme · Ages 7–11",
  subject: "Values · Citizenship · Mindfulness",
  unit: "Unit 3 · Compassion in Action",
  chapter: "Chapter 1 · Why was Babasaheb so determined to learn?",
  duration: "60 minutes",
};

const VOCAB = [
  ["dignity", "the right of every person to be treated with respect."],
  ["equality", "everyone being treated fairly and given the same chances."],
  ["constitution", "the rulebook of a country that explains everyone's rights."],
  ["compassion", "noticing when someone is hurting and wanting to help."],
];

export default function EducationSample() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("print") === "1") {
      const t = setTimeout(() => window.print(), 800);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <>
      {/* Top bar (screen only) */}
      <div className="bg-navy text-pearl py-4 print:hidden" data-testid="sample-bar">
        <div className="section-container flex items-center justify-between gap-4">
          <Link to="/education" className="inline-flex items-center gap-2 text-pearl/80 hover:text-pearl text-sm" data-testid="sample-back">
            <ArrowLeft size={16} /> Back to the series
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="sample-print-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-navy hover:bg-gold-light rounded-full text-sm font-medium"
          >
            <Printer size={16} /> Print or save as PDF
          </button>
        </div>
      </div>

      <div className="sample-doc bg-pearl text-navy">
        {/* COVER */}
        <article className="sample-page sample-cover">
          <div className="cover-band">
            <span className="cover-eyebrow">{META.series}</span>
          </div>
          <div className="cover-grid">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-saffron">{META.level}</p>
              <h1 className="cover-title">{META.chapter.split(" · ")[1]}</h1>
              <p className="cover-sub">{META.unit}</p>
              <div className="cover-meta">
                <div><span>Subject</span>{META.subject}</div>
                <div><span>Duration</span>{META.duration}</div>
                <div><span>Sample</span>Phase 1 prototype</div>
              </div>
            </div>
            <div className="cover-portrait">
              <img src={BABASAHEB_IMG} alt="Babasaheb Dr. B.R. Ambedkar" />
            </div>
          </div>
          <p className="cover-tag">Education · Equality · Enlightenment</p>
        </article>

        {/* SECTION 1 — Learning Objective */}
        <article className="sample-page">
          <Section icon={Target} number="1" title="Learning Objective" tone="objective">
            <p className="lead">
              By the end of this lesson, pupils will be able to:
            </p>
            <ul className="objectives">
              <li><strong>Explain</strong> what dignity means in their own words.</li>
              <li><strong>Describe</strong> one event from Babasaheb's childhood and how he responded to unfairness.</li>
              <li><strong>Identify</strong> one example of how dignity and equality are protected in modern Britain.</li>
            </ul>
            <Box label="Curriculum link">
              Learning grounded in lived values: dignity, fairness, kindness and citizenship.
              Connects to wider conversations about rights and responsibilities, mutual respect
              and the rule of law in modern Britain.
            </Box>
          </Section>
        </article>

        {/* SECTION 2 — Story / Case Study */}
        <article className="sample-page">
          <Section icon={BookOpen} number="2" title="Story · A Boy Who Loved to Learn" tone="story">
            <p>
              When Bhimrao was a small boy in India, he loved reading more than anything. He would walk
              for hours just to find a new book. But because of an unfair rule called the caste system,
              his school did not want him to sit on the same bench as the other children.
            </p>
            <p>
              On hot days, when the other children were given water from the school tap, Bhimrao was not
              allowed to drink. The school caretaker would not pour water into his cup. Bhimrao went home
              thirsty, but he never gave up on learning. He kept his head high, held his books tight,
              and promised himself: <em>"I will study so well, that one day I will write the rules of
              this country, so no other child will ever feel this way."</em>
            </p>
            <p>
              And he did. When India became free, Bhimrao, now called <strong>Babasaheb Dr. B.R. Ambedkar</strong>
              — was chosen to write India's Constitution. The very first words he made sure it said?
              That every child, of every group, is <strong>equal</strong>.
            </p>
            <Box label="Read aloud · Class discussion">
              How do you think young Bhimrao felt when he was refused water? What gave him the strength
              to keep going? Have you ever stood up for someone who was being treated unfairly?
            </Box>
          </Section>
        </article>

        {/* SECTION 3 — Concept Explanation */}
        <article className="sample-page">
          <Section icon={Lightbulb} number="3" title="Concept · Dignity and Equality" tone="concept">
            <p>
              <strong>Dignity</strong> means the value every person has, simply because they are a person.
              You don't have to earn dignity. You don't have to be rich, fast, clever or older. You have
              dignity from the moment you are born.
            </p>
            <p>
              <strong>Equality</strong> means treating everyone with the same care and giving them the
              same chances. It does not mean everyone is the same, we are all different!, it means
              nobody should be left out, looked down on, or refused something fair just because of who
              they are, where they were born, what their family does, or what they look like.
            </p>
            <Box label="Vocabulary">
              <dl className="vocab">
                {VOCAB.map(([term, def]) => (
                  <React.Fragment key={term}>
                    <dt>{term}</dt>
                    <dd>{def}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </Box>
          </Section>
        </article>

        {/* SECTION 4 — Real-world application */}
        <article className="sample-page">
          <Section icon={Compass} number="4" title="Real-world Application · Dignity in Britain Today" tone="context">
            <p>
              Britain has its own way of protecting dignity and equality. The law in this country says
              it is wrong to treat someone unfairly because of:
            </p>
            <ul className="bullets">
              <li>their religion or beliefs</li>
              <li>their race or where they come from</li>
              <li>their gender</li>
              <li>any disability they have</li>
              <li>their age, or other parts of who they are</li>
            </ul>
            <p>
              Schools are special places where dignity matters every single day. That is why school
              uniforms, pupil voice, anti-bullying rules, and the role of the School Council all exist —
              so every child is heard and respected.
            </p>
            <Box label="Real-life link">
              The law in Britain protects every child, including in your own school. Every teacher,
              every parent, and every pupil shares the duty of making sure no one is left out.
            </Box>
          </Section>
        </article>

        {/* SECTION 5 — Activity */}
        <article className="sample-page">
          <Section icon={Pencil} number="5" title="Activity · The Dignity Wall" tone="activity">
            <p className="lead">A 25-minute group activity for pairs or table groups.</p>
            <ol className="steps">
              <li><strong>Listen.</strong> The teacher reads the story of Bhimrao again, slowly.</li>
              <li><strong>Discuss in pairs.</strong> One thing you think was unfair, and one way Bhimrao showed strength.</li>
              <li><strong>Write.</strong> On a sticky note, finish this sentence: <em>"Everyone in our class deserves dignity because…"</em></li>
              <li><strong>Build.</strong> Together, place all sticky notes on a "Dignity Wall" at the front of the class.</li>
              <li><strong>Reflect.</strong> The class reads the wall together. What words appear most often?</li>
            </ol>
            <Box label="Resources needed">
              Sticky notes (1 per pupil), pens, a clear wall or large sheet of paper, the printed story
              from Section 2.
            </Box>
          </Section>
        </article>

        {/* SECTION 6 — Reflection Questions */}
        <article className="sample-page">
          <Section icon={MessageSquare} number="6" title="Reflection · Take it Home" tone="reflection">
            <p className="lead">Choose one question to answer in your journal, or talk about with a grown-up at home.</p>
            <ol className="reflection">
              <li>If you could change one rule at our school to make it kinder, what would it be?</li>
              <li>Babasaheb said reading was his superpower. What is your superpower?</li>
              <li>Tell someone at home about Babasaheb. What part of his story did you find the bravest?</li>
              <li>Can you name one person at school who always treats others with dignity? Why them?</li>
              <li><strong>Stretch:</strong> The Buddha and Babasaheb both believed in compassion, noticing
                when someone is hurting and wanting to help. When did you last show compassion?</li>
            </ol>
          </Section>
        </article>

        {/* SECTION 7 — Teacher Notes */}
        <article className="sample-page sample-teacher">
          <Section icon={GraduationCap} number="7" title="Teacher Notes" tone="teacher">
            <p className="text-xs uppercase tracking-[0.2em] text-saffron mb-3">Teacher Edition only</p>
            <h4>Differentiation</h4>
            <ul className="bullets">
              <li><strong>Lower-ability:</strong> use the picture-card version of the story (provided in
                Workbook p.12). Pair with stronger reader for the discussion task.</li>
              <li><strong>Higher-ability:</strong> stretch question, research one of Britain's other
                everyday rules that protect dignity, and present it to the class next lesson.</li>
              <li><strong>EAL learners:</strong> use the bilingual vocabulary card (Workbook p.14, available
                in Hindi, Marathi, Tamil, Punjabi, Polish, Romanian, Urdu).</li>
            </ul>

            <h4>Assessment for learning</h4>
            <ul className="bullets">
              <li>Use the Dignity Wall as formative assessment, note common themes and gaps.</li>
              <li>Quick exit ticket: each pupil names one thing they will do differently this week.</li>
            </ul>

            <h4>Cross-curricular links</h4>
            <ul className="bullets">
              <li><strong>English:</strong> use the story as a stimulus for narrative writing.</li>
              <li><strong>History:</strong> compare Babasaheb's story with British civil rights history.</li>
              <li><strong>Faith & belief:</strong> compare with stories of compassion in Christianity, Islam, Sikhism.</li>
            </ul>

            <h4>Sensitivity & safeguarding</h4>
            <p>
              The story touches on discrimination. Frame it as historical injustice that the law of both
              India and Britain now protects against. Be mindful of pupils with lived experience of
              prejudice, invite, but never require, personal sharing. Follow your school's wellbeing
              and safeguarding procedures if disclosure occurs.
            </p>

            <h4>Tone check</h4>
            <p>
              This chapter promotes values of mutual respect, fairness and human dignity, and supports
              pupils' wider spiritual, moral, social and cultural development. No proselytising. No
              religious conversion messaging. Multi-faith respect modelled throughout.
            </p>
          </Section>
        </article>

        {/* BACK */}
        <article className="sample-page sample-back">
          <p className="back-eyebrow">Sample chapter · Phase 1 prototype</p>
          <h2 className="back-title">This is one chapter from a planned 15-book series.</h2>
          <p className="back-body">
            The complete <strong>Ambedkarite Buddhist Educational Series, UK Edition</strong> will cover
            ages 4 to 16, with a Student Book, a Workbook and a Teacher Guide for each age group.
            Every chapter follows this same seven-step structure.
          </p>
          <Link to="/education#interest" className="back-cta print:hidden" data-testid="sample-back-cta">
            Express interest for your school →
          </Link>
          <p className="back-meta">
            Ambedkarite Buddhist Global Network UK, United Kingdom · info@ambedkaritebuddhistglobalnetworkuk.com<br/>
            <span className="italic">Education · Equality · Enlightenment</span>
          </p>
        </article>
      </div>

      <style>{`
        .sample-doc {
          max-width: 880px;
          margin: 0 auto;
          padding: 32px 16px;
        }
        .sample-page {
          background: #fff;
          border: 1px solid rgba(0,27,58,0.12);
          border-radius: 8px;
          padding: 48px 56px;
          margin-bottom: 24px;
          page-break-after: always;
          break-after: page;
          position: relative;
        }
        .sample-cover {
          background: linear-gradient(180deg, #FDFBF7 0%, #F2E8D2 100%);
        }
        .cover-band {
          border-bottom: 1px solid rgba(0,27,58,0.15);
          padding-bottom: 12px;
          margin-bottom: 32px;
        }
        .cover-eyebrow {
          font-size: 11px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #001B3A;
          font-weight: 600;
        }
        .cover-grid {
          display: grid;
          grid-template-columns: 1.3fr 1fr;
          gap: 36px;
          align-items: center;
        }
        .cover-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 48px;
          line-height: 1.05;
          color: #001B3A;
          margin: 12px 0 16px;
        }
        .cover-sub {
          font-size: 18px;
          color: #CFA052;
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          margin: 0 0 24px;
        }
        .cover-meta {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 12px;
          font-size: 13px;
          color: #4A5568;
        }
        .cover-meta > div {
          padding: 12px;
          background: rgba(255,255,255,0.6);
          border: 1px solid rgba(0,27,58,0.08);
          border-radius: 6px;
        }
        .cover-meta span {
          display: block;
          font-size: 9px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #6b7280;
          margin-bottom: 4px;
        }
        .cover-portrait {
          aspect-ratio: 4/5;
          overflow: hidden;
          border-radius: 6px;
          border: 1px solid rgba(0,27,58,0.1);
          background: #001B3A;
        }
        .cover-portrait img { width: 100%; height: 100%; object-fit: cover; object-position: 50% 30%; }
        .cover-tag {
          margin-top: 36px;
          padding-top: 16px;
          border-top: 1px solid rgba(207,160,82,0.4);
          text-align: center;
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          color: #CFA052;
          font-size: 18px;
          letter-spacing: 0.05em;
        }
        .section-block { }
        .section-head {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(0,27,58,0.1);
        }
        .section-num {
          flex-shrink: 0;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #001B3A;
          color: #FDFBF7;
          font-family: 'Cormorant Garamond', serif;
          font-size: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .section-icon {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: rgba(207,160,82,0.15);
          color: #8B6E2A;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 6px;
        }
        .section-title-block { flex: 1; }
        .section-eyebrow {
          font-size: 10px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #E07A5F;
          font-weight: 600;
        }
        .section-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 32px;
          line-height: 1.1;
          color: #001B3A;
          margin: 4px 0 0;
        }
        .section-content { font-size: 15px; line-height: 1.65; color: #1f2937; }
        .section-content p { margin: 0 0 14px; }
        .section-content .lead { font-size: 16px; color: #001B3A; font-weight: 500; }
        .section-content ul, .section-content ol { padding-left: 22px; margin: 12px 0; }
        .section-content li { margin-bottom: 6px; }
        .section-content h4 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 22px;
          color: #001B3A;
          margin: 22px 0 10px;
          padding-top: 14px;
          border-top: 1px dashed rgba(0,27,58,0.15);
        }
        .objectives li, .reflection li { margin-bottom: 10px; }
        .steps li { margin-bottom: 10px; padding-left: 4px; }
        .vocab {
          display: grid;
          grid-template-columns: 130px 1fr;
          gap: 6px 14px;
          margin: 4px 0 0;
        }
        .vocab dt {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 600;
          color: #001B3A;
        }
        .vocab dd { margin: 0; color: #4A5568; }
        .callout-box {
          margin: 16px 0;
          padding: 16px 18px;
          background: rgba(224,122,95,0.08);
          border-left: 4px solid #E07A5F;
          border-radius: 4px;
          font-size: 14px;
          color: #1f2937;
        }
        .callout-box strong.label {
          display: block;
          font-size: 10px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #C44A2D;
          margin-bottom: 6px;
        }
        .sample-teacher { background: #FFF8EC; border-color: rgba(207,160,82,0.4); }
        .sample-back { background: #001B3A; color: #FDFBF7; text-align: center; }
        .back-eyebrow { font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; color: #CFA052; }
        .back-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 36px;
          line-height: 1.15;
          margin: 16px auto 18px;
          max-width: 600px;
        }
        .back-body {
          color: rgba(253,251,247,0.85);
          max-width: 560px;
          margin: 0 auto 32px;
          font-size: 15px;
          line-height: 1.6;
        }
        .back-cta {
          display: inline-block;
          padding: 12px 28px;
          background: #CFA052;
          color: #001B3A;
          border-radius: 999px;
          text-decoration: none;
          font-weight: 600;
          margin-bottom: 36px;
        }
        .back-meta {
          padding-top: 24px;
          border-top: 1px solid rgba(253,251,247,0.2);
          font-size: 12px;
          color: rgba(253,251,247,0.7);
        }

        @media print {
          @page { size: A4; margin: 14mm; }
          html, body { background: #fff !important; }
          .sample-doc { max-width: none; margin: 0; padding: 0; }
          .sample-page {
            border: none;
            border-radius: 0;
            margin: 0;
            padding: 0 0 12mm 0;
            box-shadow: none;
          }
          .sample-cover, .sample-back, .sample-teacher { padding: 8mm 4mm; }
          .sample-page:last-child { page-break-after: auto; }
        }
      `}</style>
    </>
  );
}

function Section({ icon: Icon, number, title, tone, children }) {
  const eyebrow = {
    objective: "What pupils will learn",
    story: "Read aloud · 8 minutes",
    concept: "Concept explanation",
    context: "In Britain today",
    activity: "Group activity · 25 minutes",
    reflection: "Reflection questions",
    teacher: "Teacher edition only",
  }[tone] || "";
  return (
    <div className="section-block">
      <div className="section-head">
        <div className="section-num">{number}</div>
        <div className="section-icon"><Icon size={18} /></div>
        <div className="section-title-block">
          <span className="section-eyebrow">{eyebrow}</span>
          <h3 className="section-title">{title}</h3>
        </div>
      </div>
      <div className="section-content">{children}</div>
    </div>
  );
}

function Box({ label, children }) {
  return (
    <div className="callout-box">
      <strong className="label">{label}</strong>
      <div>{children}</div>
    </div>
  );
}
